import type { ListProjectNetworksParams, Network } from '@amarantos/api-client'
import { z } from 'zod/v3'

import { WHOLE_LIST_PER_PAGE } from '@/utils/whole-list'

const basePlanningSearchSchema = z.object({
	networkId: z.coerce.number().int().positive().optional(),
	/** The selected manhole — opens the inspector in edit mode, or anchors an add-upstream. */
	nodeId: z.coerce.number().int().positive().optional(),
	/** Inspector create mode: a standalone new manhole, or one flowing into the selected node. */
	manhole: z.enum(['create', 'add-upstream']).optional(),
	/** The selected pipe — opens the inspector in pipe-edit mode. Mutually exclusive with `nodeId`. */
	connectionId: z.coerce.number().int().positive().optional(),
	/** Inspector pipe-create mode: add a pipe between two existing manholes. */
	pipe: z.literal('create').optional(),
})

// Create/rename dialog state. The rename dialog targets the selected `networkId`, so
// the `edit` branch needs no id of its own.
const dialogSearchStateSchema = z.union([
	z.object({ dialog: z.literal('create') }),
	z.object({ dialog: z.literal('edit') }),
	z.object({ dialog: z.undefined().optional() }),
])

export const planningSearchSchema = basePlanningSearchSchema.and(dialogSearchStateSchema)

export type PlanningSearch = z.infer<typeof planningSearchSchema>

/** The fullscreen editor's two lenses, swapped by its view switch: authoring vs. the read-only status canvas. */
export const EDITOR_VIEWS = ['planning', 'preview'] as const

export type EditorView = (typeof EDITOR_VIEWS)[number]

/**
 * The fullscreen editor needs the selected network, which view it shows, and what the inspector panel
 * points at. It takes both create params: `manhole` — anchored to a card's + — and `pipe`, raised by the
 * tool box's add-pipe button between two chosen manholes.
 *
 * `manhole` restates iteration 1's enum whole rather than narrowing to the one value the editor drives.
 * Both routes name the same URL param, so the router types it once across them: a narrower literal here
 * makes every `search: (previous) => ({ ...previous })` in the editor a type error, since `previous`
 * carries the wider type from the planning route. The editor simply ignores `create` until its tool box
 * has a button to raise it.
 */
export const editorSearchSchema = z.object({
	networkId: z.coerce.number().int().positive().optional(),
	view: z.enum(EDITOR_VIEWS).default('planning'),
	/** The selected manhole — opens the inspector panel in edit mode, or anchors an add-upstream. */
	nodeId: z.coerce.number().int().positive().optional(),
	/** Inspector create mode: a new manhole flowing into the selected node (the + on its card). */
	manhole: z.enum(['create', 'add-upstream']).optional(),
	/** The selected pipe — opens the inspector panel in pipe-edit mode. Mutually exclusive with `nodeId`. */
	connectionId: z.coerce.number().int().positive().optional(),
	/** Inspector pipe-create mode: add a pipe between two existing manholes (the tool box's add-pipe button). */
	pipe: z.literal('create').optional(),
})

export type EditorSearch = z.infer<typeof editorSearchSchema>

export function toListProjectNetworksParams(): ListProjectNetworksParams {
	return {
		page: 1,
		per_page: WHOLE_LIST_PER_PAGE,
	}
}

/**
 * The network the editor opens on when the URL names none: the project's first — the same one the switch
 * lists first. `undefined` when the project has no networks, which leaves the editor on its empty state.
 */
export function resolveDefaultNetworkId(networks: Network[]): Network['id'] | undefined {
	return networks[0]?.id
}
